/**
 * ASamplesX navigation behaviour.
 *
 * The main nav points at homepage sections with absolute hrefs (`/#kontakt`),
 * so it works from any template without JavaScript. This module adds two things
 * on top, and only when they are actually useful:
 *
 *   1. On the homepage, a link whose target exists in the document scrolls to it
 *      instead of navigating - no page load for an in-page move.
 *   2. The item matching the section currently in view is marked active, so the
 *      header's underline indicator tracks the reader.
 *
 * The document is the scroll container here, not `.page-wrapper` - that element
 * carries no overflow of its own and is taller than the viewport. Both the
 * scroll call and the IntersectionObserver therefore work against the viewport,
 * which is also the one root that stays correct if Horizon's shell ever moves
 * the overflow back onto the wrapper: the sections leave the viewport either way.
 */

(function () {
  const links = Array.from(document.querySelectorAll('[data-asx-nav-link]'));
  if (!links.length) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * The in-page target for a link, or null when it points elsewhere.
   * @param {HTMLAnchorElement} link
   * @returns {HTMLElement | null}
   */
  function targetFor(link) {
    const href = link.getAttribute('href') || '';
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return null;

    // Only treat it as in-page when the path before the hash is this page.
    const path = href.slice(0, hashIndex);
    if (path && path !== window.location.pathname && path !== '/') return null;

    const id = href.slice(hashIndex + 1);
    if (!id) return null;

    try {
      return document.getElementById(decodeURIComponent(id));
    } catch {
      return null;
    }
  }

  for (const link of links) {
    link.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      const target = targetFor(link);
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });

      // Keep the address bar honest without triggering another jump.
      history.replaceState(null, '', link.getAttribute('href'));

      // Move focus so keyboard and screen-reader users land where sighted users do.
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  /* ---------------------------------------------------------------- *
   * Active section tracking
   * ---------------------------------------------------------------- */
  const watched = links
    .map((link) => ({ link, target: targetFor(link) }))
    .filter((entry) => entry.target);

  if (!watched.length || !('IntersectionObserver' in window)) return;

  // The home item has no in-page target, so it is never "in view" - it is what
  // is active while none of the sections are. Liquid marks it as the current
  // page, which is right on arrival and wrong the moment a section takes over,
  // so the two states are managed together here rather than fighting.
  const rootPath = new URL(document.baseURI).pathname;
  const homeLinks = links.filter((link) => {
    if (targetFor(link)) return false;
    const href = link.getAttribute('href') || '';
    if (href.indexOf('#') !== -1) return false;
    try {
      return new URL(href, document.baseURI).pathname === rootPath;
    } catch {
      return false;
    }
  });

  /** @param {HTMLAnchorElement[]} active */
  function mark(active) {
    for (const link of links) {
      const on = active.indexOf(link) !== -1;
      link.classList.toggle('menu-list__link--active', on);
      if (on) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
  }

  // The observer reports only what CHANGED, so the set of what is on screen is
  // kept here - otherwise a section scrolling out would leave the last match
  // highlighted with nothing to replace it.
  const onScreen = new Set();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }

      // The topmost section on screen wins, so overlapping sections do not
      // fight over the indicator.
      const top = watched
        .filter((entry) => onScreen.has(entry.target))
        .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top)[0];

      mark(top ? [top.link] : homeLinks);
    },
    { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
  );

  for (const { target } of watched) observer.observe(target);
})();
